/**
 * One session's own popover: the date, the hour where there is one, and „Przywróć".
 *
 * ── The path a drag cannot take ──────────────────────────────────────────────────────────────
 * A drag says "this day". A trainer who needs "this day, at 19:00 instead of 17:30" has no gesture
 * for it, and mobile solves it exactly here — `ScheduleStep.tsx` keeps the standing weekday time
 * and a per-occurrence `{date, time}` override beside it, with a restore that clears it. Both
 * paths write the same `OccurrenceOverride`; `model/occurrenceEdit.ts` decides what is written.
 *
 * ── Base UI supplies the behaviour and we supply every visual byte ──────────────────────────
 * Focus management, dismissal, positioning and the ARIA wiring are `Popover`'s. The popup is NOT
 * given a `key` — a keyed popup closes and reopens itself, focus lands on the inert surface behind
 * it and the next keypress is dropped.
 */

import { useState } from 'react';
import { Popover } from '@base-ui/react/popover';
import { useTranslation } from 'react-i18next';

import ember from '~/shared/ui/emberButton.module.css';

import { occurrenceEditRefusalMessage } from '../model/assignRefusalCopy';
import type { OccurrenceEditFields, OccurrenceEditRefusal } from '../model/occurrenceEdit';
import type { ProjectionChip } from '../model/projectionGrid';

import { StartMonthPicker } from './StartMonthPicker';
import styles from './AssignPlanDialog.module.css';

/**
 * No dots in this window.
 *
 * Under the form's picker a dot means "the plan trains on this weekday", which is what makes a
 * mid-week start's cost visible before it is chosen. Here the trainer is moving ONE session, and
 * the weekdays the plan happens to programme say nothing about where this one may land.
 */
const NO_DOTS: readonly number[] = [];

interface OccurrenceEditPopoverProps {
  readonly chip: ProjectionChip;
  readonly fields: OccurrenceEditFields;
  readonly onSave: (next: { readonly date: string; readonly time: string | null }) => OccurrenceEditRefusal | null;
  readonly onRestore: () => void;
  readonly onClose: () => void;
  /** The chip element this popover is anchored to. */
  readonly anchor: Element | null;
}

export function OccurrenceEditPopover({
  chip,
  fields,
  onSave,
  onRestore,
  onClose,
  anchor,
}: OccurrenceEditPopoverProps) {
  const { t, i18n } = useTranslation();
  const [date, setDate] = useState(fields.date);
  const [time, setTime] = useState(fields.time ?? '');
  const [refusal, setRefusal] = useState<OccurrenceEditRefusal | null>(null);

  return (
    <Popover.Root
      open
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <Popover.Portal>
        <Popover.Positioner anchor={anchor} side="right" align="start" sideOffset={8}>
          <Popover.Popup className={styles.editPopup}>
            <Popover.Title className={styles.editTitle}>
              {t('web:assign.edit.title', { title: chip.title })}
            </Popover.Title>

            {/* The form's own month picker, not `<input type="date">`: one window, one date
              * pattern. The native control also opens the BROWSER's calendar over a dialog whose
              * other date field is this grid, and it offers every day in its range — including the
              * ones before the plan starts, which the save refuses. `fields.window` is already
              * floored at the start date, so a day drawn here is a day that can be saved. */}
            <div className={styles.editField}>
              <span className={styles.editLabel}>{t('web:assign.edit.date')}</span>
              <StartMonthPicker
                value={date}
                window={fields.window}
                programmedWeekdays={NO_DOTS}
                onChange={(iso) => {
                  setDate(iso);
                  setRefusal(null);
                }}
                locked={false}
                reasonId={null}
                helpId={null}
                gridLabel={t('web:assign.edit.dateGridLabel', { title: chip.title })}
                locale={i18n.language}
              />
            </div>

            {/* Absent, not disabled: a remote assignment is scheduled date-only server-side, so an
              * hour set here would be silently discarded on the wire. */}
            {fields.time !== null && (
              <label className={styles.editField}>
                <span className={styles.editLabel}>{t('web:assign.edit.time')}</span>
                <input
                  className={styles.editInput}
                  type="time"
                  value={time}
                  onChange={(event) => {
                    setTime(event.target.value);
                    setRefusal(null);
                  }}
                />
              </label>
            )}

            {/* Always in the tree so the id never points at nothing, and `role="alert"` because the
              * refusal arrives while focus is on Zapisz. */}
            <p className={refusal === null ? styles.editHidden : styles.editRefusal} role="alert">
              {refusal === null ? '' : occurrenceEditRefusalMessage(refusal, t)}
            </p>

            <div className={styles.editActions}>
              {fields.restorable && (
                <button
                  type="button"
                  className={styles.editRestore}
                  onClick={() => {
                    onRestore();
                    onClose();
                  }}
                >
                  {t('web:assign.edit.restore')}
                </button>
              )}
              <span className={styles.editSpacer} />
              <button type="button" className={styles.editCancel} onClick={onClose}>
                {t('web:assign.edit.cancel')}
              </button>
              <button
                type="button"
                className={`${String(ember.ember)} ${String(styles.editSave)}`}
                onClick={() => {
                  const refused = onSave({ date, time: fields.time === null ? null : time });
                  if (refused === null) onClose();
                  else setRefusal(refused);
                }}
              >
                {t('web:assign.edit.save')}
              </button>
            </div>
          </Popover.Popup>
        </Popover.Positioner>
      </Popover.Portal>
    </Popover.Root>
  );
}
